/**
 * @file 文档页页面组件
 * @author wanzhongyun
 */

import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { message, Select } from 'antd';
import copy from 'copy-text-to-clipboard';
import {
    CaretDownOutlined
} from '@ant-design/icons';
import classNames from 'classnames';

import hljs from '../../utils/highlight';
import mathJax from '../../utils/mathjax/mathjax';

import Header from '../../components/header/HeaderWithStore';
import Footer from '../../components/footer/Footer';
import useDocumentStore from './documentHooks';

import ReactStickyPolyfill from '../../components/react-sticky-polyfill/ReactStickyPolyfill';
import DocBreadcrumb from './components/doc-breadcrumb/DocBreadcrumb';
import DocTopMenu from './components/doc-top-menu/DocTopMenu';
import DocRightMenu from './components/doc-right-menu/DocRightMenu';
import DocLeftMenu from './components/doc-left-menu/DocLeftMenu';


const Document: React.FC = () => {
    const {
        menuHook,
        versionListHook,
        langListHook,
        breadcrumbsListHook,
        documentArticle,

        scrollToAnchor
    } = useDocumentStore();

    const {
        leftMenuShow,
        topMenuList,
        subMenuList,
        anchorMenuList,
        topMenuSelectedKeys,
        subMenuSelectedKeys,
        subMenuOpenKeys,
        anchorSelectedKeys,
        anchorOpenKeys,

        setSubMenuOpenKeys,
        switchLeftMenuShow
    } = menuHook;

    const {
        versionList,
        versionSelectedKeys,
        setVersionSelectedKeys
    } = versionListHook;

    const {
        langList,
        langSelectedKeys,
        setLangSelectedKeys
    } = langListHook;

    const breadcrumbsList = breadcrumbsListHook.breadcrumbsList;

    const pageMainRef = useRef<HTMLDivElement>(null);
    const topRef = useRef<HTMLDivElement>(null);

    const handleOnVersionChange = useCallback(
        (key: string) => setVersionSelectedKeys(key),
        [setVersionSelectedKeys]
    );
    const handleOnLangChange = useCallback(
        (key: string) => setLangSelectedKeys(key),
        [setLangSelectedKeys]
    );

    const topSelectContainer = useCallback(() => {
        return topRef.current || document.querySelector('.paddle-doc-page-top');
    }, []);

    const markdownBodyRef = useRef<HTMLDivElement>(null);

    useLayoutEffect(() => {
        if (markdownBodyRef.current) {
            markdownBodyRef.current.innerHTML = '';
            if (documentArticle) {
                markdownBodyRef.current.appendChild(documentArticle);
            }
        }
    }, [documentArticle]);

    useEffect(() => {
        if (documentArticle && markdownBodyRef.current) {
            markdownBodyRef.current.querySelectorAll<HTMLElement>('.highlight').forEach(block => {
                hljs.highlightBlock(block);
            });
        }
    }, [documentArticle]);

    useEffect(() => {
        (async () => {
            const theMathJax = await mathJax;
            await theMathJax.Hub.Queue(['Typeset', theMathJax.Hub]);
            scrollToAnchor();
        })();
    }, [documentArticle, scrollToAnchor]);

    // 绑定别名的点击事件
    useEffect(() => {
        const dom = markdownBodyRef.current;
        if (dom) {
            const func = (event: MouseEvent) => {
                const getPath = (element: HTMLElement, path: HTMLElement[] = []) => {
                    path.push(element);
                    if (element.parentElement) {
                        getPath(element.parentElement, path);
                    }
                    return path;
                };

                const targetDom = event.target as HTMLDivElement;
                const parents = getPath(targetDom);
                const labelDom = parents.find(e => e.matches('.paddle-doc-alias-tags-label'));
                if (labelDom) {
                    const classNameArr = labelDom.className.split(' ').filter(i => i);
                    if (classNameArr.indexOf('open') > -1) {
                        labelDom.className = classNameArr.filter(c => c !== 'open').join(' ');
                    }
                    else {
                        labelDom.className = labelDom.className + ' open';
                    }
                }
            };
            dom.addEventListener('click', func);
            return () => {
                dom.removeEventListener('click', func);
            };
        }
    }, [documentArticle]);


    const [themeDark, setThemeDark] = useState<boolean>(() => {
        if (window.localStorage) {
            const storage = window.localStorage;
            return !!storage.getItem('document-theme-dark');
        }
        return false;
    });

    const onThemeDarkChange = useCallback(() => {
        setThemeDark((old) => {
            if (window.localStorage) {
                const storage = window.localStorage;
                storage.setItem('document-theme-dark', !old ? '1' : '');
            }
            return !old;
        });
    }, [setThemeDark]);
    const isPromptCode = (code: string) => {
        return /\n>>>\s?/.test("\n" + code);
    }
    // 代码文本块格式转换
    const codeTextFormatChange = (code: string) => {

        if (!isPromptCode(code)) {
            return code;
        }
        const lines = code.split("\n");
        const newLines = [];

        for (const line of lines) {
            if (line.startsWith(">>> ") || line.startsWith("... ")) {
                newLines.push(line.slice(4));
            }
            if (line === ">>>" || line === "..." || line === "") {
                newLines.push("");
            }
        }

        return newLines.join("\n");
    };
    const copyBtnClick = useCallback((event: MouseEvent) => {
        const codeWrap = event.target as HTMLDivElement;
        const codeDom = codeWrap.parentNode?.parentNode?.querySelector('pre');
        const text = codeDom?.innerText || '';
        const text2 = text.length > 20 ? `${text.slice(0, 20)}...` : text;
        if(text.indexOf('>>>') !== -1) {
            message.info(`${text.slice(4, 20)} Copied to clipboard`);
        }else {
            message.info(`${text2} Copied to clipboard`);
        };
        copy(codeTextFormatChange(text));
    }, []);

    // 复制按钮、主题按钮
    useEffect(() => {
        const dom = markdownBodyRef.current;
        if (dom) {
            const btnDomList: HTMLElement[] = [];
            const allCodeList = Array.from(dom.querySelectorAll<HTMLDivElement>('.highlight'));
            allCodeList.forEach((codeDiv) => {
                const btnWrap = document.createElement('div');
                btnWrap.className = 'btn-wrap';

                (() => {
                    const newBtn = document.createElement('div');
                    btnDomList.push(newBtn);
                    newBtn.className = 'code-copy-btn';
                    newBtn.title = 'Copy to clipboard';
                    btnWrap.appendChild(newBtn);
                    codeDiv.appendChild(btnWrap);
                    newBtn.addEventListener('click', copyBtnClick);
                })();

                (() => {
                    const newBtn = document.createElement('div');
                    btnDomList.push(newBtn);
                    newBtn.className = 'code-theme-btn';
                    btnWrap.appendChild(newBtn);
                    codeDiv.appendChild(btnWrap);
                    newBtn.addEventListener('click', onThemeDarkChange);
                })();
            });
            const inputCodeList = Array.from(dom.querySelectorAll<HTMLDivElement>('.highlight-text .highlight'));
            inputCodeList.forEach((codeDiv) => {
                const btnWrap = document.createElement('div');
                btnWrap.className = 'btn-wrap';

                const newBtn = document.createElement('div');
                btnDomList.push(newBtn);
                newBtn.className = 'code-theme-btn';
                btnWrap.appendChild(newBtn);
                codeDiv.appendChild(btnWrap);
                newBtn.addEventListener('click', onThemeDarkChange);
            });

            return () => {
                btnDomList.forEach((btnDom) => {
                    btnDom.parentNode?.removeChild(btnDom);
                });
            };
        }
    }, [documentArticle, onThemeDarkChange, copyBtnClick]);

    return (
        <div className="page paddle-doc-page">
            <Header />
            <ReactStickyPolyfill>
                <div className="paddle-doc-page-top">
                    <div className="paddle-doc-page-top-inner">
                        <div className="paddle-doc-page-version-select-wrap">
                            <Select
                                className="paddle-doc-page-version-select"
                                dropdownMatchSelectWidth={false}
                                value={versionSelectedKeys}
                                dropdownClassName="paddle-doc-page-version-select-drop"
                                getPopupContainer={topSelectContainer}
                                onChange={handleOnVersionChange}
                                optionLabelProp="label"
                                size="large"
                                {...{ suffixIcon: <CaretDownOutlined /> }}
                            >
                                {versionList.map(item => (
                                    <Select.Option
                                        key={item.key}
                                        value={item.key}
                                        label={item.title}
                                    >
                                        {item.title}
                                    </Select.Option>
                                ))}
                            </Select>
                        </div>
                        <DocTopMenu
                            topMenuSelectedKeys={topMenuSelectedKeys}
                            topMenuList={topMenuList}
                        />
                        <div className="paddle-doc-page-lang-select-wrap">
                            <Select
                                className="paddle-doc-page-lang-select"
                                dropdownMatchSelectWidth={false}
                                value={langSelectedKeys}
                                dropdownClassName="paddle-doc-page-lang-select-drop"
                                getPopupContainer={topSelectContainer}
                                size="large"
                                onChange={handleOnLangChange}
                                optionLabelProp="label"
                            >
                                {langList.map(item => (
                                    <Select.Option
                                        key={item.key}
                                        value={item.key}
                                        label={item.title}
                                    >
                                        {item.title}
                                    </Select.Option>
                                ))}
                            </Select>
                        </div>
                    </div>
                </div>
            </ReactStickyPolyfill>

            <div className={
                classNames(
                    'paddle-doc-page-container',
                    {
                        'paddle-doc-page-container-no-left': subMenuList.length === 0,
                        'paddle-doc-page-container-no-right': anchorMenuList.length === 0
                    }
                )}
            >
                <div
                    className={classNames(
                        'paddle-doc-page-main',
                        {
                            'paddle-doc-page-left-menu-show': leftMenuShow
                        }
                    )}
                    ref={pageMainRef}
                >
                    <div className="paddle-doc-page-left-background" />
                    <div className="paddle-doc-page-left">
                        <ReactStickyPolyfill>
                            <div className="paddle-doc-page-left-menu-wrap">
                                <DocLeftMenu
                                    subMenuOpenKeys={subMenuOpenKeys}
                                    subMenuSelectedKeys={subMenuSelectedKeys}
                                    subMenuList={subMenuList}
                                    setSubMenuOpenKeys={setSubMenuOpenKeys}
                                />
                            </div>
                        </ReactStickyPolyfill>
                    </div>

                    <div className="paddle-doc-page-right">
                        <ReactStickyPolyfill>
                            <div className="paddle-doc-page-right-menu-wrap">
                                <div className="paddle-doc-page-right-menu-index">
                                    目录
                                </div>
                                <DocRightMenu
                                    anchorOpenKeys={anchorOpenKeys}
                                    anchorSelectedKeys={anchorSelectedKeys}
                                    anchorMenuList={anchorMenuList}
                                />
                            </div>
                        </ReactStickyPolyfill>
                    </div>

                    <div className="paddle-doc-page-center">
                        <ReactStickyPolyfill>
                            <div className="paddle-doc-page-left-menu-switch-wrap">
                                <div className="paddle-doc-page-left-menu-switch" onClick={switchLeftMenuShow} />
                            </div>
                        </ReactStickyPolyfill>
                        <div className="paddle-doc-page-breadcrumb-wrap">
                            <DocBreadcrumb
                                breadcrumbsList={breadcrumbsList}
                            />
                        </div>

                        <div
                            ref={markdownBodyRef}
                            className={classNames(
                                'paddle-doc-page-content',
                                'markdown-body',
                                themeDark && 'theme-code-dark'
                            )}
                        >
                        </div>
                    </div>
                </div>
            </div>
            <Footer />
        </div>
    );
};

export default Document;
